/* ═══════════════════════════════════════════════════════════════
   The Designer — the zone model behind click-to-style.

   One tiny address book for the paper's non-body zones. A BAND is what
   you click (the header strip, the footer strip, the pre/post-body
   strips); a ZONE KEY is where its tokens actually live on the forma
   (`headerOddLeft` in headerFooter, `bodyTopCenter` in special, …).
   Everything in Design mode — selection, palette targeting, drops,
   clipboard — speaks through these helpers so the storage shape stays
   in exactly one place (the same shape shared/zone-layout.ts reads).
   ═══════════════════════════════════════════════════════════════ */

import type { StandardConfig } from '../../shared/types';

export type BandKind = 'header' | 'footer' | 'pre' | 'post';
export type PageVariant = 'first' | 'odd' | 'even';
export type ZoneColumn = 'L' | 'C' | 'R';

/** What is selected for styling on the Design-mode paper. */
export type StyleSelection =
  | { kind: 'level'; level: number }
  | { kind: 'zone'; band: BandKind }
  | null;

const VARIANT_INFIX: Record<PageVariant, string> = {
  first: 'First',
  odd: 'Odd',
  even: 'Even',
};

const COLUMN_SUFFIX: Record<ZoneColumn, string> = { L: 'Left', C: 'Center', R: 'Right' };

export const BAND_LABEL: Record<BandKind, string> = {
  header: 'Header',
  footer: 'Footer',
  pre: 'Pre-body',
  post: 'Post-body',
};

export const COLUMN_LABEL: Record<ZoneColumn, string> = { L: 'Left', C: 'Center', R: 'Right' };

/** The storage key of one band column. Pre/post ignore the page variant. */
export function zoneKeyFor(band: BandKind, variant: PageVariant, col: ZoneColumn): string {
  if (band === 'pre') return `bodyTop${COLUMN_SUFFIX[col]}`;
  if (band === 'post') return `post${COLUMN_SUFFIX[col]}`;
  return `${band}${VARIANT_INFIX[variant]}${COLUMN_SUFFIX[col]}`;
}

/** Which store a zone key lives in on the forma. */
export function zoneSource(zoneKey: string): 'hf' | 'sp' {
  return zoneKey.startsWith('header') || zoneKey.startsWith('footer') ? 'hf' : 'sp';
}

/** Read a zone's token array (never mutates; missing zone = empty). */
export function readZone(forma: StandardConfig, zoneKey: string): string[] {
  const store =
    zoneSource(zoneKey) === 'hf'
      ? (forma.headerFooter as unknown as Record<string, string[] | undefined>)
      : (forma.special as unknown as Record<string, string[] | undefined>);
  const arr = store?.[zoneKey];
  return Array.isArray(arr) ? arr : [];
}

/** A new forma with one zone replaced. Pure — the input is untouched. */
export function writeZone(forma: StandardConfig, zoneKey: string, arr: string[]): StandardConfig {
  if (zoneSource(zoneKey) === 'hf') {
    return { ...forma, headerFooter: { ...forma.headerFooter, [zoneKey]: arr } };
  }
  return { ...forma, special: { ...forma.special, [zoneKey]: arr } };
}

/** The page variants this forma actually has, in edit order. */
export function pageVariants(forma: StandardConfig | null): PageVariant[] {
  const out: PageVariant[] = [];
  if (forma?.docSettings?.firstPageDifferent) out.push('first');
  out.push('odd');
  if (forma?.docSettings?.oddEvenDifferent) out.push('even');
  return out;
}

/** Map the Design-mode page variant onto the ZoneBand's Word variant name. */
export function bandVariantName(variant: PageVariant): 'First' | 'Odd' | 'Even' {
  return VARIANT_INFIX[variant] as 'First' | 'Odd' | 'Even';
}

/* ─── Zone edits — the SAME semantics the classic forma canvas applies
       (PageCanvas's insertAtInZone / replaceAtInZone / zoneDrop), expressed
       as pure StandardConfig → StandardConfig transforms so the Designer's
       draft pipeline can undo/save them as single edits. ─── */

/** `token:<zoneKey>:<idx>` → its parts. */
export function parseTokenKey(key: string): { zoneKey: string; idx: number } {
  const r = key.slice('token:'.length);
  const li = r.lastIndexOf(':');
  return { zoneKey: r.slice(0, li), idx: parseInt(r.slice(li + 1), 10) };
}

export function zoneAppend(forma: StandardConfig, zoneKey: string, value: string): StandardConfig {
  return writeZone(forma, zoneKey, [...readZone(forma, zoneKey), value]);
}

export function zoneRemoveAt(forma: StandardConfig, zoneKey: string, idx: number): StandardConfig {
  const arr = [...readZone(forma, zoneKey)];
  if (idx < 0 || idx >= arr.length) return forma;
  arr.splice(idx, 1);
  return writeZone(forma, zoneKey, arr);
}

export function zoneUpdateAt(
  forma: StandardConfig,
  zoneKey: string,
  idx: number,
  value: string
): StandardConfig {
  const arr = [...readZone(forma, zoneKey)];
  if (idx < 0 || idx >= arr.length) return forma;
  arr[idx] = value;
  return writeZone(forma, zoneKey, arr);
}

/** Insert at a position, optionally pulling the value out of a source zone
    first (drag-move), optionally starting a new visual line. Mirrors the
    classic canvas's insertAtInZone. */
export function zoneInsertAt(
  forma: StandardConfig,
  zoneKey: string,
  insertIdx: number,
  value: string,
  sourceZone?: string | null,
  sourceIdx?: number | null,
  newLine?: boolean
): StandardConfig {
  let next = forma;
  let idx = insertIdx;
  if (sourceZone != null && sourceIdx != null) {
    next = zoneRemoveAt(next, sourceZone, sourceIdx);
    if (sourceZone === zoneKey && sourceIdx < idx) idx--;
  }
  const arr = [...readZone(next, zoneKey)];
  idx = Math.max(0, Math.min(idx, arr.length));
  if (value === '\n') {
    arr.splice(idx, 0, '\n');
  } else if (newLine && idx > 0 && arr[idx - 1] !== '\n') {
    arr.splice(idx, 0, '\n', value);
  } else if (newLine && idx === 0 && arr.length > 0 && arr[0] !== '\n') {
    arr.splice(idx, 0, value, '\n');
  } else {
    arr.splice(idx, 0, value);
  }
  return writeZone(next, zoneKey, arr);
}

/** Replace one token, optionally consuming a dragged source token. */
export function zoneReplaceAt(
  forma: StandardConfig,
  zoneKey: string,
  replaceIdx: number,
  value: string,
  sourceZone?: string | null,
  sourceIdx?: number | null
): StandardConfig {
  let next = forma;
  if (sourceZone != null && sourceIdx != null && sourceZone !== zoneKey) {
    next = zoneRemoveAt(next, sourceZone, sourceIdx);
  }
  return zoneUpdateAt(next, zoneKey, replaceIdx, value);
}

/** Move (or copy) tokens by key into a target zone — appended, in key order. */
export function zoneMoveTokens(
  forma: StandardConfig,
  targetZoneKey: string,
  keys: string[],
  copy: boolean
): StandardConfig {
  const items = keys.filter((k) => k.startsWith('token:')).map(parseTokenKey);
  const values = items
    .map(({ zoneKey, idx }) => readZone(forma, zoneKey)[idx])
    .filter((v): v is string => typeof v === 'string');
  if (!values.length) return forma;
  let next = forma;
  if (!copy) {
    const byZone: Record<string, number[]> = {};
    for (const { zoneKey, idx } of items) (byZone[zoneKey] ||= []).push(idx);
    for (const [zk, idxs] of Object.entries(byZone)) {
      const arr = [...readZone(next, zk)];
      idxs
        .sort((a, b) => b - a)
        .forEach((i) => {
          if (i >= 0 && i < arr.length) arr.splice(i, 1);
        });
      next = writeZone(next, zk, arr);
    }
  }
  return writeZone(next, targetZoneKey, [...readZone(next, targetZoneKey), ...values]);
}

/** Apply a value-transform to every given token key (multi-select format). */
export function zoneApplyToKeys(
  forma: StandardConfig,
  keys: string[],
  fn: (value: string) => string
): StandardConfig {
  const byZone: Record<string, number[]> = {};
  for (const k of keys) {
    if (!k.startsWith('token:')) continue;
    const { zoneKey, idx } = parseTokenKey(k);
    (byZone[zoneKey] ||= []).push(idx);
  }
  let next = forma;
  for (const [zk, idxs] of Object.entries(byZone)) {
    const arr = [...readZone(next, zk)];
    let touched = false;
    for (const i of idxs) {
      if (i >= 0 && i < arr.length && typeof arr[i] === 'string') {
        arr[i] = fn(arr[i]);
        touched = true;
      }
    }
    if (touched) next = writeZone(next, zk, arr);
  }
  return next;
}

/** Delete every given token key. */
export function zoneDeleteKeys(forma: StandardConfig, keys: string[]): StandardConfig {
  const byZone: Record<string, number[]> = {};
  for (const k of keys) {
    if (!k.startsWith('token:')) continue;
    const { zoneKey, idx } = parseTokenKey(k);
    (byZone[zoneKey] ||= []).push(idx);
  }
  let next = forma;
  for (const [zk, idxs] of Object.entries(byZone)) {
    const arr = [...readZone(next, zk)];
    idxs
      .sort((a, b) => b - a)
      .forEach((i) => {
        if (i >= 0 && i < arr.length) arr.splice(i, 1);
      });
    next = writeZone(next, zk, arr);
  }
  return next;
}
